/**
 * Audio feedback utilities for scan operations
 * Provides consistent sound effects across the application
 */

let audioContext: AudioContext | null = null;

/**
 * Get or create the global AudioContext instance
 * Supports both standard and webkit AudioContext
 */
const getAudioContext = (): AudioContext => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  return audioContext;
};

/**
 * Play a success sound - positive, short and friendly
 * Two ascending tones (C5 and E5) for a pleasant chime effect
 */
export const playSuccessSound = (): void => {
  try {
    const ctx = getAudioContext();
    
    // Create a pleasant, short "ding" sound with two tones
    const oscillator1 = ctx.createOscillator();
    const oscillator2 = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator1.connect(gainNode);
    oscillator2.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    // Pleasant ascending tones (like a success chime)
    oscillator1.frequency.value = 523.25; // C5
    oscillator1.type = 'sine';
    
    oscillator2.frequency.value = 659.25; // E5
    oscillator2.type = 'sine';
    
    // Quick, friendly attack and decay
    gainNode.gain.setValueAtTime(0, ctx.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.25, ctx.currentTime + 0.05);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
    
    oscillator1.start(ctx.currentTime);
    oscillator2.start(ctx.currentTime);
    oscillator1.stop(ctx.currentTime + 0.2);
    oscillator2.stop(ctx.currentTime + 0.2);
  } catch (error) {
    console.error('Error playing success sound:', error);
  }
};

/**
 * Play an error sound - negative, warning sound
 * Lower frequency sawtooth wave for a more dissonant, alerting effect
 */
export const playErrorSound = (): void => {
  try {
    const ctx = getAudioContext();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    // Lower frequency, more dissonant sound
    oscillator.frequency.value = 200;
    oscillator.type = 'sawtooth'; // More harsh than sine
    
    // Quick attack, slower decay for warning effect
    gainNode.gain.setValueAtTime(0, ctx.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.05);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.4);
  } catch (error) {
    console.error('Error playing error sound:', error);
  }
};

/**
 * Cleanup function to close the AudioContext when no longer needed
 * Should be called when the component unmounts
 */
export const cleanupAudio = (): void => {
  if (audioContext) {
    audioContext.close().catch((error) => {
      console.error('Error closing AudioContext:', error);
    });
    audioContext = null;
  }
};



